/**
 * ABC Tool Component - Loads /tools/abctool.html in an iframe inside the app shell
 * (Header + Footer from App.js).
 *
 * Author: Markus van Kempen - markus.van.kempen@gmail.com
 * Last Updated: July 2026
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import SEO from './SEO';
import { useTheme } from '../context/ThemeContext';
import RegisterNudgeBubble from './RegisterNudgeBubble';
import './ABCTool.css';

// embed=1 skips the top-level 301 to /abc-tool so the iframe can load the tool HTML.
// Bump ABC_TOOL_REV when abctool.html layout/UX changes (browsers cache /tools/* for days).
const ABC_TOOL_REV = '20260725g';
const ABC_TOOL_SRC = `/tools/abctool.html?embed=1&v=${ABC_TOOL_REV}`;
const MOBILE_MQ = '(max-width: 768px)';

const ABCTool = () => {
  const { currentTheme, theme } = useTheme();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const iframeRef = useRef(null);
  const containerRef = useRef(null);
  const observersRef = useRef([]);
  const rafRef = useRef(0);
  const measuringRef = useRef(false);
  const lastHeightRef = useRef(0);

  const applyThemeToIframe = useCallback(() => {
    const iframe = iframeRef.current;
    const colors = theme?.colors;
    if (!iframe || !colors) return;

    const payload = {
      type: 'abctool-theme',
      themeId: currentTheme,
      colors,
    };

    try {
      const doc = iframe.contentDocument;
      if (doc?.documentElement) {
        Object.entries(colors).forEach(([property, value]) => {
          doc.documentElement.style.setProperty(property, value);
        });
        doc.documentElement.setAttribute('data-theme', currentTheme);
        if (doc.body) {
          doc.body.className = `theme-${currentTheme}`;
        }
        if (typeof doc.defaultView?.__abctoolApplySiteTheme === 'function') {
          doc.defaultView.__abctoolApplySiteTheme(payload);
        }
      }
    } catch (_) {
      /* ignore */
    }

    try {
      iframe.contentWindow?.postMessage(payload, window.location.origin);
    } catch (_) {
      /* ignore */
    }
  }, [currentTheme, theme]);

  const clearObservers = () => {
    observersRef.current.forEach((obs) => {
      try {
        obs.disconnect();
      } catch (_) {
        /* ignore */
      }
    });
    observersRef.current = [];
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = 0;
    }
  };

  const syncIframeHeight = useCallback(() => {
    const iframe = iframeRef.current;
    const container = containerRef.current;
    if (!iframe || !container || measuringRef.current) return;

    measuringRef.current = true;
    try {
      const doc = iframe.contentDocument;
      if (!doc) return;

      // Collapse first so previous iframe size doesn't inflate measurement
      const prev = iframe.style.height;
      iframe.style.height = '1px';

      const tune = doc.querySelector('.tune-page');
      const height = Math.ceil(
        Math.max(
          tune?.scrollHeight || 0,
          doc.body?.scrollHeight || 0,
          doc.documentElement?.scrollHeight || 0
        )
      );

      if (height > 0) {
        const next = height + 4;
        // Ignore sub-pixel / 1–2px churn that retriggers ResizeObserver
        if (Math.abs(next - lastHeightRef.current) > 2) {
          iframe.style.height = `${next}px`;
          lastHeightRef.current = next;
          container.classList.add('abctool-sized');
        } else {
          iframe.style.height = prev || `${lastHeightRef.current}px`;
        }
      } else if (prev) {
        iframe.style.height = prev;
      }
    } catch (_) {
      /* cross-origin — leave CSS fallback height */
    } finally {
      // Release after this frame so RO callbacks from our own write are ignored
      requestAnimationFrame(() => {
        measuringRef.current = false;
      });
    }
  }, []);

  const scheduleSync = useCallback(() => {
    if (rafRef.current) return;
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = 0;
      syncIframeHeight();
    });
  }, [syncIframeHeight]);

  const attachSizeObservers = useCallback(() => {
    clearObservers();
    const iframe = iframeRef.current;
    if (!iframe) return;

    try {
      const doc = iframe.contentDocument;
      const tune = doc?.querySelector('.tune-page');
      if (!tune) {
        syncIframeHeight();
        return;
      }

      // Observe the content root only (not html/body) to reduce RO loops
      const ro = new ResizeObserver(() => scheduleSync());
      ro.observe(tune);
      observersRef.current.push(ro);

      const mo = new MutationObserver(() => scheduleSync());
      mo.observe(tune, {
        childList: true,
        subtree: true,
        attributes: true,
        characterData: true,
      });
      observersRef.current.push(mo);
    } catch (_) {
      /* ignore */
    }

    scheduleSync();
  }, [scheduleSync, syncIframeHeight]);

  useEffect(() => {
    const onResize = () => scheduleSync();
    window.addEventListener('resize', onResize);
    const mq = window.matchMedia(MOBILE_MQ);
    const onMq = () => scheduleSync();
    if (mq.addEventListener) mq.addEventListener('change', onMq);
    else mq.addListener(onMq);

    // Messages from iframe: jump-scroll + theme request
    const onMessage = (event) => {
      if (event.origin !== window.location.origin) return;
      const data = event.data;
      if (!data || typeof data !== 'object') return;

      if (data.type === 'abctool-theme-request') {
        applyThemeToIframe();
        return;
      }

      if (data.type !== 'abctool-scroll-to') return;
      const iframe = iframeRef.current;
      if (!iframe || typeof data.top !== 'number') return;
      const iframeTop = iframe.getBoundingClientRect().top + window.scrollY;
      const headerOffset = window.matchMedia(MOBILE_MQ).matches ? 64 : 76;
      window.scrollTo({
        top: Math.max(0, iframeTop + data.top - headerOffset),
        behavior: 'smooth',
      });
      // Content may have changed visibility — remeasure after jump
      setTimeout(scheduleSync, 100);
    };
    window.addEventListener('message', onMessage);

    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('message', onMessage);
      if (mq.removeEventListener) mq.removeEventListener('change', onMq);
      else mq.removeListener(onMq);
      clearObservers();
    };
  }, [scheduleSync, applyThemeToIframe]);

  // Keep iframe in sync when user switches theme in the floating widget
  useEffect(() => {
    if (!loading) {
      applyThemeToIframe();
    }
  }, [currentTheme, theme, loading, applyThemeToIframe]);

  return (
    <>
      <SEO
        title="MIDI to ABC Converter | Free Online ABC Tool | Cranford Publications"
        description="Convert MIDI files to ABC notation online. Load, play, edit, and download Celtic fiddle tunes in ABC format — free tool from Cranford Publications."
        canonical="/abc-tool"
        keywords="MIDI to ABC, ABC converter, ABC notation, fiddle tunes ABC, midi2abc, Celtic music notation, Cranford Publications"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'WebApplication',
          name: 'MIDI to ABC Converter',
          description:
            'Convert MIDI files to ABC notation online. Load, play, edit, and download Celtic fiddle tunes in ABC format.',
          url: 'https://www.cranfordpub.ca/abc-tool',
          applicationCategory: 'MultimediaApplication',
          operatingSystem: 'Any',
          offers: {
            '@type': 'Offer',
            price: '0',
            priceCurrency: 'CAD',
          },
          provider: {
            '@type': 'Organization',
            name: 'Cranford Publications',
            url: 'https://www.cranfordpub.ca',
          },
        }}
      />
      <div className="abctool-container" ref={containerRef}>
        {loading && !error && (
          <div className="abctool-loading">
            <p>Loading ABC Tool...</p>
          </div>
        )}
        {error && (
          <div className="abctool-error">
            <p>{error}</p>
            <button type="button" onClick={() => window.location.reload()}>
              Reload Page
            </button>
          </div>
        )}
        <iframe
          id="abctool-iframe"
          ref={iframeRef}
          title="ABC Tool - MIDI to ABC Converter"
          className="abctool-iframe"
          src={ABC_TOOL_SRC}
          allow="autoplay"
          style={{ display: error ? 'none' : 'block', opacity: loading ? 0 : 1 }}
          onLoad={() => {
            setLoading(false);
            setError(null);
            applyThemeToIframe();
            requestAnimationFrame(() => {
              applyThemeToIframe();
              attachSizeObservers();
              setTimeout(scheduleSync, 80);
              setTimeout(scheduleSync, 350);
            });
          }}
          onError={() => {
            setLoading(false);
            setError('Failed to load ABC Tool. Please try refreshing the page.');
          }}
        />
      </div>
      <RegisterNudgeBubble />
    </>
  );
};

export default ABCTool;
